<template>
  <div>
    <NavigationBreadcrumb :items="breadcrumb_items"></NavigationBreadcrumb>
    <div class="general-report-container">
      <!-- Hero Header -->
      <header class="report-hero">
        <span class="report-hero__icon">
          <b-icon icon="bar-chart-line-fill"></b-icon>
        </span>
        <div class="report-hero__text">
          <h1 class="report-hero__title">
            {{ $t("toolbar.reports.reportsTitle") }}
          </h1>
          <p class="report-hero__subtitle noprint">
            {{ $t("toolbar.reports.reportsSubtitle") }}
          </p>
        </div>
      </header>

      <!-- Paso 1: selección del tipo de reporte (tarjetas) -->
      <section class="report-picker noprint">
        <div class="step-head">
          <span class="step-badge">1</span>
          <h2 class="step-title">
            {{ $t("toolbar.reports.selectReportType") }}
          </h2>
          <AlternativeNameButton
            v-if="selected_report_table_name"
            :table_name="selected_report_table_name"
            :allows_crud="allows_crud"
          ></AlternativeNameButton>
        </div>
        <div class="report-tiles">
          <button
            v-for="option in report_type_options"
            :key="option.value"
            type="button"
            class="report-tile"
            :class="{
              'report-tile--active': selected_report_type === option.value,
            }"
            @click="selected_report_type = option.value"
          >
            <span class="report-tile__icon">
              <b-icon :icon="reportTypeIcon(option.value)"></b-icon>
            </span>
            <span class="report-tile__label">{{ option.text }}</span>
            <b-icon
              v-if="selected_report_type === option.value"
              icon="check-circle-fill"
              class="report-tile__check"
            ></b-icon>
          </button>
        </div>
      </section>

      <!-- Paso 2: filtros y acciones -->
      <section v-if="selected_report_type" class="filters-card noprint">
        <div class="step-head">
          <span class="step-badge">2</span>
          <h2 class="step-title">
            {{ $t("toolbar.reports.configureFilters") }}
          </h2>
        </div>

        <Filters
          :max_campuses="max_campuses"
          :max_careers="max_careers"
          :max_egress_profiles="max_egress_profiles"
          :max_sections="max_sections"
          :max_matters="max_matters"
          :max_modalities="max_modalities"
          :max_periods="max_periods"
          :max_levels="max_levels"
          :max_study_units="max_study_units"
          :max_cohorts="max_cohorts"
          :max_cohort_index="max_cohort_index"
          :max_professors="max_professors"
          :max_cycles="max_cycles"
          :max_student_status="max_student_status"
          :max_is_active_section="max_is_active_section"
          :use_calculated_cohorts="use_calculated_cohorts"
          :load_cohorts="load_cohorts"
          :disabled="!selected_report_type"
          :required_selectors="required_selectors"
          :show_selectors="show_selectors"
          :clear_filters="clear_filters"
          :filters_to_clear="filters_to_clear"
          :selected_report_type="selected_report_type"
          :initial_filter_ids="initial_filter_ids"
          @campuses="(x) => (campuses_ = x)"
          @careers="(x) => (careers_ = x)"
          @egress_profiles="(x) => (egress_profiles_ = x)"
          @levels="(x) => (levels_ = x)"
          @matters="(x) => (matters_ = x)"
          @periods="(x) => (periods_ = x)"
          @sections="(x) => (sections_ = x)"
          @study_units="(x) => (study_units_ = x)"
          @cohorts="(x) => (cohorts_ = x)"
          @cohort_indices="(x) => (cohort_indices_ = x)"
          @professors="(x) => (professors_ = x)"
          @cycles="(x) => (cycles_ = x)"
          @student_status="(x) => (student_status_ = x)"
          @is_active_section="(x) => (is_active_section_ = x)"
        >
        </Filters>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <ButtonMedium
            :tooltip_text="generateButtonTooltip"
            :text_button="$t('toolbar.reports.generateReport')"
            :click_button="fetchReport"
            :variant="'primary'"
            :disabled_button="!requirementFulfilled || loading"
          >
          </ButtonMedium>
          <ButtonMedium
            :tooltip_text="``"
            :text_button="$t('toolbar.reports.clearFilters')"
            :click_button="clearFilters"
            :variant="'secondary'"
          >
          </ButtonMedium>
        </div>
      </section>

      <!-- Estado inicial / bienvenida -->
      <template v-if="!selected_report_type">
        <section class="welcome-card noprint">
          <div class="welcome-content">
            <span class="welcome-icon">
              <b-icon icon="clipboard-data"></b-icon>
            </span>
            <h3 class="welcome-title">
              {{
                $t("toolbar.reports.welcomeMessage", {
                  firstName: user.first_name,
                  lastName: user.last_name,
                })
              }}
            </h3>
            <p class="welcome-description">
              {{ $t("toolbar.reports.welcomeDescription") }}
            </p>
            <ul class="welcome-features">
              <li
                v-for="option in first_report_type_options"
                :key="option.value"
                class="feature-item"
              >
                <b-icon
                  icon="check-circle-fill"
                  class="feature-item__icon"
                ></b-icon>
                <span>{{ option.text }}</span>
              </li>
            </ul>
          </div>
          <div class="welcome-illustration">
            <img
              class="welcome-image"
              src="@/assets/img-general-report.png"
              alt="Ilustración de seguimiento de aprendizaje"
            />
          </div>
        </section>
      </template>

      <LegendFilters
        v-if="selectors"
        :key="`LegendFilters-${report_key}`"
        :campuses_="selectors.selected_campuses"
        :careers_="selectors.selected_careers"
        :egress_profiles_="selectors.selected_egress_profiles"
        :levels_="selectors.selected_levels"
        :matters_="selectors.selected_matters"
        :periods_="selectors.selected_periods"
        :sections_="selectors.selected_sections"
        :study_units_="selectors.selected_study_units"
        :cohorts_="selectors.selected_cohorts"
        :cycles_="selectors.selected_cycles"
        :student_status_="selectors.selected_student_status"
        :show_selectors="show_selectors"
      ></LegendFilters>
      <AssessmentRangeSection
        v-if="
          selectors != null &&
          selected_report_type == 1 &&
          show_range_grades_section &&
          selectors.selected_matters &&
          selectors.selected_matters.length == 1 &&
          selectors.selected_sections &&
          selectors.selected_sections.length > 0
        "
        :key="report_key"
        :sections_ids="selectors.selected_sections.map((x) => x.id)"
        :egress_profile_matter="selectors.selected_matters[0]"
        :campuses_="selectors.selected_campuses"
        :egress_profiles_="selectors.selected_egress_profiles"
        :cohorts_="selectors.selected_cohorts"
        :cohort_indices_="selectors.selected_cohort_indices"
        :student_status_="selectors.selected_student_status"
        :levels_="selectors.selected_levels"
        :matters_="selectors.selected_matters"
        :periods_="selectors.selected_periods"
        :sections_="selectors.selected_sections"
        :show_section_navigation="true"
      ></AssessmentRangeSection>
      <AssessmentCompetenceEgrPro
        v-if="
          selected_report_type == 2 &&
          selectors != null &&
          selectors.selected_egress_profiles &&
          selectors.selected_egress_profiles.length == 1 &&
          selectors.selected_cohorts &&
          selectors.selected_cohorts.length > 0
        "
        :key="report_key"
        :egress_profile="selectors.selected_egress_profiles[0]"
        :campuses_="selectors.selected_campuses"
        :egress_profiles_="selectors.selected_egress_profiles"
        :cohorts_="selectors.selected_cohorts"
        :cohort_indices_="selectors.selected_cohort_indices"
        :cycles_="selectors.selected_cycles"
        :student_status_="selectors.selected_student_status"
      ></AssessmentCompetenceEgrPro>
      <AssessmentGroupCompEgrPro
        v-if="
          selected_report_type == 3 &&
          selectors != null &&
          selectors.selected_egress_profiles &&
          selectors.selected_egress_profiles.length == 1 &&
          selectors.selected_cohorts &&
          selectors.selected_cohorts.length > 0
        "
        :key="report_key"
        :egress_profile="selectors.selected_egress_profiles[0]"
        :campuses_="selectors.selected_campuses"
        :egress_profiles_="selectors.selected_egress_profiles"
        :cohorts_="selectors.selected_cohorts"
        :cohort_indices_="selectors.selected_cohort_indices"
        :cycles_="selectors.selected_cycles"
        :student_status_="selectors.selected_student_status"
      ></AssessmentGroupCompEgrPro>
      <AssessmentGroupStudyUnit
        v-if="
          selected_report_type == 4 &&
          selectors != null &&
          selectors.selected_egress_profiles &&
          selectors.selected_egress_profiles.length == 1 &&
          selectors.selected_cohorts &&
          selectors.selected_cohorts.length > 0
        "
        :key="report_key"
        :levels_="selectors.selected_levels"
        :egress_profile="selectors.selected_egress_profiles[0]"
        :egress_profile_matter="selectors.selected_matters[0]"
        :cohorts_="selectors.selected_cohorts"
        :cohort_indices_="selectors.selected_cohort_indices"
        :cycles_="selectors.selected_cycles"
        :periods_="selectors.selected_periods"
        :sections_="selectors.selected_sections"
        :student_status_="selectors.selected_student_status"
      ></AssessmentGroupStudyUnit>
      <AssessmentGroupEvaluationCriteria
        v-if="
          selected_report_type == 5 &&
          selectors != null &&
          selectors.selected_egress_profiles &&
          selectors.selected_egress_profiles.length == 1 &&
          selectors.selected_matters &&
          selectors.selected_matters.length > 0
        "
        :key="report_key"
        :egress_profile="selectors.selected_egress_profiles[0]"
        :egress_profile_matter="selectors.selected_matters[0]"
        :campuses_="selectors.selected_campuses"
        :periods_="selectors.selected_periods"
        :study_units_="selectors.selected_study_units"
        :student_status_="selectors.selected_student_status"
      ></AssessmentGroupEvaluationCriteria>
      <EvaluationGradeReport
        v-if="
          selected_report_type == 6 &&
          selectors != null &&
          selectors.selected_egress_profiles &&
          selectors.selected_egress_profiles.length == 1 &&
          selectors.selected_cohorts &&
          selectors.selected_cohorts.length > 0
        "
        :key="report_key"
        :egress_profile="selectors.selected_egress_profiles[0]"
        :cohorts_="selectors.selected_cohorts"
        :cohort_indices_="selectors.selected_cohort_indices"
        :campuses_="selectors.selected_campuses"
        :student_status_="selectors.selected_student_status"
      ></EvaluationGradeReport>
      <ProfessorProgressionReport
        v-if="
          selected_report_type == 7 &&
          selectors != null &&
          selectors.selected_is_active_section &&
          selectors.selected_is_active_section.length > 0
        "
        :key="report_key"
        :egress_profiles="selectors.selected_egress_profiles"
        :campuses_="selectors.selected_campuses"
        :egress_profiles_="selectors.selected_egress_profiles"
        :levels_="selectors.selected_levels"
        :matters_="selectors.selected_matters"
        :periods_="selectors.selected_periods"
        :sections_="selectors.selected_sections"
        :professors_="selectors.selected_professors"
        :is_active_section_="selectors.selected_is_active_section"
      ></ProfessorProgressionReport>
      <IndividualMatterGradesReport
        v-if="
          selected_report_type == 8 &&
          selectors != null &&
          selectors.selected_egress_profiles &&
          selectors.selected_egress_profiles.length == 1 &&
          selectors.selected_cohorts &&
          selectors.selected_cohorts.length > 0
        "
        :key="report_key"
        :campuses_="selectors.selected_campuses"
        :egress_profile="selectors.selected_egress_profiles[0]"
        :cohorts_="selectors.selected_cohorts"
        :cohort_indices_="selectors.selected_cohort_indices"
        :student_status_="selectors.selected_student_status"
      ></IndividualMatterGradesReport>
    </div>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { showLoadingAlert, EventBus } from "@/utils/utils";

export default {
  name: "GeneralReport",
  components: {
    Filters: () => import("@/components/reports/Filters"),
    LegendFilters: () =>
      import("@/components/reports/TeachingReport/LegendFilters"),
    AssessmentRangeSection: () =>
      import(
        "@/components/reports/TeachingReport/AssessmentRangeSection/AssessmentRangeSection.vue"
      ),
    AssessmentCompetenceEgrPro: () =>
      import(
        "@/components/reports/TeachingReport/AssessmentCompetenceEgrPro/AssessmentCompetenceEgrPro.vue"
      ),
    AssessmentGroupCompEgrPro: () =>
      import(
        "@/components/reports/TeachingReport/AssessmentGroupCompEgrPro/AssessmentGroupCompEgrPro.vue"
      ),
    AssessmentGroupStudyUnit: () =>
      import(
        "@/components/reports/TeachingReport/AssessmentGroupStudyUnit/AssessmentGroupStudyUnit.vue"
      ),
    AssessmentGroupEvaluationCriteria: () =>
      import(
        "@/components/reports/TeachingReport/AssessmentGroupEvaluationCriteria/AssessmentGroupEvaluationCriteria.vue"
      ),
    EvaluationGradeReport: () =>
      import(
        "@/components/reports/TeachingReport/EvaluationGradeReport/EvaluationGradeReport.vue"
      ),
    ProfessorProgressionReport: () =>
      import(
        "@/components/reports/TeachingReport/ProfessorProgressionReport/ProfessorProgressionReport.vue"
      ),
    IndividualMatterGradesReport: () =>
      import(
        "@/components/reports/TeachingReport/IndividualMatterGradesReport/IndividualMatterGradesReport.vue"
      ),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
    NavigationBreadcrumb: () =>
      import("@/components/reusable/NavigationBreadcrumb"),
    AlternativeNameButton: () =>
      import("@/components/reusable/AlternativeNameButton"),
  },
  props: {},
  data() {
    return {
      selected_report_type: null,
      max_campuses: "all",
      max_careers: "all",
      max_egress_profiles: "all",
      max_sections: "all",
      max_matters: "all",
      max_modalities: "all",
      max_periods: "all",
      max_levels: "all",
      max_study_units: "all",
      max_cohorts: "all",
      max_cohort_index: "all",
      max_professors: "all",
      max_cycles: "all",
      max_student_status: "all",
      max_is_active_section: "all",
      use_calculated_cohorts: false,
      campuses_: null,
      careers_: null,
      egress_profiles_: null,
      levels_: null,
      matters_: null,
      periods_: null,
      sections_: null,
      study_units_: null,
      cohorts_: null,
      cohort_indices_: null,
      professors_: null,
      cycles_: null,
      student_status_: null,
      is_active_section_: null,
      chartdata: null,
      // component_key: 0,
      report_key: 0,
      selectors: null,
      clear_filters: false,
      filters_to_clear: [],
      disable_clear_btn: false,
      required_selectors:
        "campuses careers egress_profiles matters sections levels periods study_units cohorts student_status is_active_section cycles",
      show_selectors: "",
      loading: false,
      load_cohorts: false,
      show_range_grades_section: false,
      initial_filter_ids: {},
      is_restoring: false,
      sync_timeout: null,
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
    }),
    allows_crud() {
      return !!(this.user && this.user.is_superuser);
    },
    selected_report_table_name() {
      const table_names = {
        1: "manual.reporte.assessment_by_matter",
        2: "manual.reporte.assessment_by_competence",
        3: "manual.reporte.assessment_group_competence",
        4: "manual.reporte.assessment_group_ra_micro",
        5: "manual.reporte.assessment_group_criteria",
        6: "manual.reporte.average_grades",
        7: "manual.reporte.progression",
        8: "manual.reporte.individual_grades",
      };
      return table_names[this.selected_report_type] || null;
    },
    report_type_options() {
      const options = [
        {
          value: 1,
          order: 3,
          text: this.$getVisibleNames(
            "manual.reporte.assessment_by_matter",
            false,
            this.$t("toolbar.reports.assessmentByMatter", {
              matter: this.$getVisibleNames(
                "mesh.egressprofilematter",
                false,
                "Asignatura"
              ),
              egressProfile: this.$getVisibleNames(
                "mesh.egressprofile",
                false,
                "Perfil de egreso"
              ),
            })
          ),
        },
        {
          value: 2,
          order: 2,
          text: this.$getVisibleNames(
            "manual.reporte.assessment_by_competence",
            false,
            this.$t("toolbar.reports.assessmentByCompetence", {
              competences: this.$getVisibleNames(
                "mesh.competence",
                true,
                "Competencias"
              ),
              egressProfile: this.$getVisibleNames(
                "mesh.egressprofile",
                false,
                "Perfil de egreso"
              ),
            })
          ),
        },
        {
          value: 3,
          order: 1,
          text: this.$getVisibleNames(
            "manual.reporte.assessment_group_competence",
            false,
            this.$t("toolbar.reports.assessmentGroupCompetence", {
              competences: this.$getVisibleNames(
                "mesh.competence",
                true,
                "Competencias"
              ),
              egressProfile: this.$getVisibleNames(
                "mesh.egressprofile",
                false,
                "Perfil de egreso"
              ),
            })
          ),
        },
        {
          value: 4,
          order: 4,
          text: this.$getVisibleNames(
            "manual.reporte.assessment_group_ra_micro",
            false,
            this.$t("toolbar.reports.assessmentGroupRaMicro", {
              raMicro: this.$getVisibleNames(
                "teaching.ramicro",
                false,
                "RA Micro"
              ),
              matter: this.$getVisibleNames(
                "mesh.egressprofilematter",
                false,
                "Asignatura"
              ),
            })
          ),
        },
        {
          value: 5,
          order: 5,
          text: this.$getVisibleNames(
            "manual.reporte.assessment_group_criteria",
            false,
            this.$t("toolbar.reports.assessmentGroupCriteria", {
              evaluationCriteria: this.$getVisibleNames(
                "manual.criterio_de_evaluacion",
                false,
                "Criterio de Evaluación"
              ),
              matter: this.$getVisibleNames(
                "mesh.egressprofilematter",
                false,
                "Asignatura"
              ),
            })
          ),
        },
        {
          value: 6,
          order: 6,
          text: this.$getVisibleNames(
            "manual.reporte.average_grades",
            false,
            this.$t("toolbar.reports.averageGradesReport", {
              egressProfile: this.$getVisibleNames(
                "mesh.egressprofile",
                false,
                "Perfil de Egreso"
              ),
            })
          ),
        },
        {
          value: 7,
          order: 8,
          text: this.$getVisibleNames(
            "manual.reporte.progression",
            false,
            this.$t("toolbar.reports.progressionReport", {
              evaluations: this.$getVisibleNames(
                "evaluations2.evaluation",
                true,
                "evaluaciones"
              ),
            })
          ),
        },
        {
          value: 8,
          order: 7,
          text: this.$getVisibleNames(
            "manual.reporte.individual_grades",
            false,
            this.$t("toolbar.reports.reportIndividualGrades", {
              matter: this.$getVisibleNames(
                "mesh.egressprofilematter",
                false,
                "Asignatura"
              ),
            })
          ),
        },
      ];
      return options.sort((a, b) => a.order - b.order);
    },
    // Los 3 primeros reportes (mismo orden que las tarjetas de selección),
    // usados en el estado de bienvenida como ejemplos.
    first_report_type_options() {
      return this.report_type_options.slice(0, 3);
    },
    requirementFulfilled() {
      if (this.selected_report_type == 1) {
        if (!this.egress_profiles_ || !this.egress_profiles_.length)
          return false;
        // if (!this.periods_ || !this.periods_.length) return false;
        // if (!this.levels_ || !this.levels_.length) return false;
        // if (!this.cohorts_ || !this.cohorts_.length) return false;
        // if (!this.study_units_ || !this.study_units_.length) return false;
        if (!this.sections_ || !this.sections_.length) return false;
        if (!this.matters_ || !this.matters_.length) return false;
        return true;
      } else if (this.selected_report_type == 2) {
        if (!this.egress_profiles_ || !this.egress_profiles_.length)
          return false;
        // Sin competencias en el perfil no hay objetos que mostrar
        if (this.noCompetencesToShow) return false;
        if (!this.cohorts_ || !this.cohorts_.length) return false;
        if (!this.student_status_ || !this.student_status_.length) return false;
        return true;
      } else if (this.selected_report_type == 3) {
        if (!this.egress_profiles_ || !this.egress_profiles_.length)
          return false;
        // Sin competencias en el perfil no hay objetos que mostrar
        if (this.noCompetencesToShow) return false;
        if (!this.cohorts_ || !this.cohorts_.length) return false;
        if (!this.student_status_ || !this.student_status_.length) return false;
        return true;
      } else if (this.selected_report_type == 4) {
        if (!this.egress_profiles_ || !this.egress_profiles_.length)
          return false;
        if (!this.cohorts_ || !this.cohorts_.length) return false;
        if (!this.periods_ || !this.periods_.length) return false;
        if (!this.matters_ || !this.matters_.length) return false;
        if (!this.sections_ || !this.sections_.length) return false;
        return true;
      } else if (this.selected_report_type == 5) {
        if (!this.egress_profiles_ || !this.egress_profiles_.length)
          return false;
        if (!this.periods_ || !this.periods_.length) return false;
        if (!this.matters_ || !this.matters_.length) return false;
        if (!this.study_units_ || !this.study_units_.length) return false;
        return true;
      } else if (this.selected_report_type == 6) {
        if (!this.egress_profiles_ || !this.egress_profiles_.length)
          return false;
        if (!this.cohorts_ || !this.cohorts_.length) return false;
        return true;
      } else if (this.selected_report_type == 7) {
        // if (!this.egress_profiles_ || !this.egress_profiles_.length)
        //   return false;
        // if (!this.professors_ || !this.professors_.length) return false;
        if (!this.is_active_section_ || !this.is_active_section_.length)
          return false;
        return true;
      } else if (this.selected_report_type == 8) {
        if (!this.egress_profiles_ || !this.egress_profiles_.length)
          return false;
        if (!this.cohorts_ || !this.cohorts_.length) return false;
        return true;
      }
      // else if (this.selected_report_type == 3) {
      //   if (!this.egress_profiles_ || !this.egress_profiles_.length)
      //     return false;
      //   if (!this.levels_ || !this.levels_.length) return false;
      //   if (!this.cohorts_ || !this.cohorts_.length) return false;
      //   return true;
      // }
      return false;
    },
    breadcrumb_items() {
      return [
        { text: this.$t("reusable.breadcrumb.home"), to: "/home" },
        { text: this.$t("toolbar.reports.reportsTitle") },
      ];
    },
    // true cuando el reporte es por competencia y el perfil no tiene ninguna
    noCompetencesToShow() {
      if (this.selected_report_type != 2 && this.selected_report_type != 3)
        return false;
      if (!this.egress_profiles_ || !this.egress_profiles_.length) return false;
      return (
        !this.egress_profiles_[0].competences ||
        !this.egress_profiles_[0].competences.length
      );
    },
    // Motivo por el que el boton "Generar Reporte" queda deshabilitado
    generateButtonTooltip() {
      if (this.noCompetencesToShow)
        return this.$t("toolbar.reports.noEgressProfileCompetencesTooltip", {
          egressProfile: this.$getVisibleNames(
            "mesh.egressprofile",
            false,
            "Perfil de Egreso"
          ),
          competences: this.$getVisibleNames(
            "mesh.competence",
            true,
            "Competencias"
          ),
        });
      return "";
    },
  },
  methods: {
    /**
     * Ícono (bootstrap-icons) que representa cada tipo de reporte en las
     * tarjetas de selección. Fallback a un ícono genérico si no está mapeado.
     */
    reportTypeIcon(value) {
      const icons = {
        1: "journal-text",
        2: "diagram-3",
        3: "grid-3x3-gap",
        4: "bar-chart-line",
        5: "check2-square",
        6: "card-checklist",
        7: "graph-up",
        8: "person-lines-fill",
      };
      return icons[value] || "file-earmark-text";
    },
    /**
     * Restaura el estado de los filtros desde los query params de la URL.
     * Lee `report_type` y los IDs de cada filtro (formato: "id1,id2,id3").
     * Pasa los IDs al componente Filters mediante `initial_filter_ids` para
     * que los selectores se pre-seleccionen de forma progresiva conforme
     * las opciones estén disponibles.
     *
     * Ejemplo de URL esperada:
     *   /reports/?report_type=1&campuses=5,12&egress_profiles=3&matters=42&sections=10,11
     *
     * Activa `is_restoring` durante 5s para evitar que `syncFiltersToURL`
     * sobreescriba la URL mientras los filtros se están cargando.
     */
    restoreFiltersFromURL() {
      const query = this.$route.query;
      if (!query.report_type) return;
      this.is_restoring = true;
      this.selected_report_type = Number(query.report_type);
      const filterKeys = [
        "campuses",
        "careers",
        "egress_profiles",
        "levels",
        "periods",
        "matters",
        "sections",
        "study_units",
        "cohorts",
        "cohort_index",
        "professors",
        "cycles",
        "student_status",
        "is_active_section",
      ];
      const ids = {};
      filterKeys.forEach((key) => {
        if (query[key]) {
          // Centinela "all": selecciona todas las opciones disponibles una vez
          // cargadas (útil para filtros dinámicos como cohortes/índices, cuyos
          // valores no se conocen al construir el enlace).
          if (query[key] === "all") {
            ids[key] = "all";
            return;
          }
          const values = Array.isArray(query[key])
            ? query[key]
            : query[key].split(",");
          ids[key] = values.map(Number).filter((n) => !isNaN(n));
        }
      });
      this.initial_filter_ids = ids;
      setTimeout(() => {
        this.is_restoring = false;
      }, 5000);
    },
    /**
     * Sincroniza el estado actual de los filtros seleccionados hacia los
     * query params de la URL usando `router.replace` (sin crear historial).
     *
     * - Se ejecuta con debounce de 300ms para evitar actualizaciones excesivas.
     * - No se ejecuta mientras `is_restoring` esté activo.
     * - Compara los query params actuales con los nuevos para evitar
     *   llamadas innecesarias al router.
     * - Los IDs se serializan como strings separados por comas: "1,2,3".
     */
    syncFiltersToURL() {
      if (this.is_restoring) return;
      clearTimeout(this.sync_timeout);
      this.sync_timeout = setTimeout(() => {
        const query = {};
        if (this.selected_report_type) {
          query.report_type = String(this.selected_report_type);
        }
        const filterConfigs = [
          { key: "campuses", data: this.campuses_, idField: "id" },
          {
            key: "egress_profiles",
            data: this.egress_profiles_,
            idField: "id",
          },
          { key: "levels", data: this.levels_, idField: "id" },
          { key: "periods", data: this.periods_, idField: "id" },
          { key: "matters", data: this.matters_, idField: "id" },
          { key: "sections", data: this.sections_, idField: "id" },
          { key: "study_units", data: this.study_units_, idField: "id" },
          { key: "cohorts", data: this.cohorts_, idField: "cohort" },
          {
            key: "cohort_index",
            data: this.cohort_indices_,
            idField: "cohort_index",
          },
          { key: "professors", data: this.professors_, idField: "id" },
          { key: "cycles", data: this.cycles_, idField: "id" },
          { key: "student_status", data: this.student_status_, idField: "id" },
          {
            key: "is_active_section",
            data: this.is_active_section_,
            idField: "id",
          },
        ];
        filterConfigs.forEach(({ key, data, idField }) => {
          if (data && data.length > 0) {
            query[key] = data.map((item) => String(item[idField])).join(",");
          }
        });
        const currentQuery = this.$route.query;
        const hasChanged =
          Object.keys(query).some((k) => query[k] !== currentQuery[k]) ||
          Object.keys(currentQuery).some((k) => query[k] !== currentQuery[k]);
        if (hasChanged) {
          this.$router.replace({ query }).catch(() => {});
        }
      }, 300);
    },
    resetSettings() {
      this.max_campuses = "all";
      this.max_careers = "all";
      this.max_egress_profiles = "all";
      this.max_sections = "all";
      this.max_matters = "all";
      this.max_modalities = "all";
      this.max_periods = "all";
      this.max_levels = "all";
      this.max_study_units = "all";
      this.max_cohorts = "all";
      this.max_cohort_index = "all";
      this.max_professors = "all";
      this.max_cycles = "all";
      this.use_calculated_cohorts = false;
      this.load_cohorts = false;
      this.show_range_grades_section = false;
    },
    clearUnusedFilters(reportType) {
      const filtersPerReport = {
        1: [
          "campuses",
          "egress_profiles",
          "cohorts",
          "cohort_index",
          "levels",
          "periods",
          "matters",
          "sections",
          "student_status",
        ],
        2: [
          "campuses",
          "egress_profiles",
          "cohorts",
          "cohort_index",
          "student_status",
          "cycles",
        ],
        3: ["egress_profiles", "cohorts", "cohort_index", "student_status"],
        4: [
          "campuses",
          "egress_profiles",
          "cohorts",
          "cohort_index",
          "levels",
          "matters",
          "periods",
          "sections",
          "student_status",
        ],
        5: [
          "campuses",
          "egress_profiles",
          "levels",
          "matters",
          "periods",
          "study_units",
          "student_status",
        ],
        6: [
          "campuses",
          "egress_profiles",
          "cohorts",
          "cohort_index",
          "student_status",
        ],
        7: [
          "campuses",
          "egress_profiles",
          "levels",
          "matters",
          "periods",
          "sections",
          "professors",
          "is_active_section",
        ],
        8: [
          "campuses",
          "egress_profiles",
          "cohorts",
          "cohort_index",
          "student_status",
        ],
      };
      const usedFilters = filtersPerReport[reportType] || [];
      const allFilters = [
        "campuses",
        "careers",
        "egress_profiles",
        "levels",
        "periods",
        "matters",
        "sections",
        "study_units",
        "cohorts",
        "cohort_index",
        "professors",
        "cycles",
        "student_status",
        "is_active_section",
        "modalities",
      ];
      this.filters_to_clear = allFilters.filter(
        (f) => !usedFilters.includes(f)
      );
    },
    /**
     * Limpia todos los filtros seleccionados y resetea la URL
     * eliminando todos los query params.
     */
    clearFilters() {
      this.clear_filters = true;
      setTimeout(() => {
        this.clear_filters = false;
      }, 1000);
      this.show_range_grades_section = false;
      this.chartdata = null;
      this.selectors = null;
      this.$router.replace({ query: {} }).catch(() => {});
    },
    fetchReport() {
      if (this.loading) return;
      this.selectors = {
        selected_campuses:
          this.campuses_ && this.campuses_.length > 0
            ? [...this.campuses_]
            : [],
        selected_careers:
          this.careers_ && this.careers_.length > 0 ? [...this.careers_] : [],
        selected_egress_profiles:
          this.egress_profiles_ && this.egress_profiles_.length > 0
            ? [...this.egress_profiles_]
            : [],
        selected_levels:
          this.levels_ && this.levels_.length > 0 ? [...this.levels_] : [],
        selected_matters:
          this.matters_ && this.matters_.length > 0 ? [...this.matters_] : [],
        selected_periods:
          this.periods_ && this.periods_.length > 0 ? [...this.periods_] : [],
        selected_sections:
          this.sections_ && this.sections_.length > 0
            ? [...this.sections_]
            : [],
        selected_study_units:
          this.study_units_ && this.study_units_.length > 0
            ? [...this.study_units_]
            : [],
        selected_cohorts:
          this.cohorts_ && this.cohorts_.length > 0 ? [...this.cohorts_] : [],
        selected_professors:
          this.professors_ && this.professors_.length > 0
            ? [...this.professors_]
            : [],
        selected_cohort_indices:
          this.cohort_indices_ && this.cohort_indices_.length > 0
            ? [...this.cohort_indices_]
            : [],
        selected_cycles:
          this.cycles_ && this.cycles_.length > 0 ? [...this.cycles_] : [],
        selected_student_status:
          this.student_status_ && this.student_status_.length > 0
            ? [...this.student_status_]
            : [],
        selected_is_active_section:
          this.is_active_section_ && this.is_active_section_.length > 0
            ? [...this.is_active_section_]
            : [],
      };
      this.loading = true;
      // El botón permanece deshabilitado hasta que el reporte hijo
      // (AssessmentRangeTable, EvaluationGradeReportTable, etc.) emita
      // "report_request_finished" al terminar su petición real (éxito o
      // error). No se libera cuando el usuario solo cierra el swal con la
      // (x): así se evita disparar múltiples peticiones de reportes
      // pesados mientras la anterior sigue en curso en el backend.
      EventBus.$off("report_request_finished", this.onReportRequestFinished);
      EventBus.$once("report_request_finished", this.onReportRequestFinished);
      showLoadingAlert(
        this.$t("toolbar.reports.generatingReport"),
        this.$t("toolbar.reports.pleaseWait"),
        true
      );
      if (this.selected_report_type == 1) {
        this.fetchRangeGradesSections();
      } else {
        this.report_key++;
        this.chartdata = [];
      }
    },
    fetchRangeGradesSections() {
      this.report_key++;
      this.show_range_grades_section = true;
      this.chartdata = [];
    },
    onReportRequestFinished() {
      this.loading = false;
    },
  },
  watch: {
    selected_report_type() {
      this.chartdata = null;
      this.selectors = null;
      this.show_range_grades_section = false;
      this.resetSettings();
      if (this.selected_report_type == 1) {
        this.max_egress_profiles = 1;
        this.max_matters = 1;
        // this.max_sections = 1;
        this.max_periods = 1;
        this.max_levels = 1;
        this.use_calculated_cohorts = true;
        this.show_selectors =
          "campuses egress_profiles cohorts cohort_index levels periods matters sections student_status";
        this.required_selectors =
          "egress_profiles periods levels matters sections";
      }
      if (this.selected_report_type == 2) {
        this.max_egress_profiles = 1;
        this.use_calculated_cohorts = true;
        this.show_selectors =
          "campuses egress_profiles cohorts cohort_index student_status cycles";
        this.required_selectors = "egress_profiles cohorts student_status";
      }
      if (this.selected_report_type == 3) {
        this.max_egress_profiles = 1;
        this.use_calculated_cohorts = true;
        this.show_selectors =
          "egress_profiles cohorts cohort_index student_status";
        this.required_selectors = "egress_profiles cohorts student_status";
      }
      if (this.selected_report_type == 4) {
        this.max_egress_profiles = 1;
        this.max_matters = 1;
        this.max_levels = 1;
        this.max_periods = 1;
        this.use_calculated_cohorts = true;
        this.show_selectors =
          "campuses egress_profiles cohorts cohort_index levels matters periods sections student_status";
        this.required_selectors =
          "egress_profiles cohorts levels matters periods sections";
      }
      if (this.selected_report_type == 5) {
        this.max_egress_profiles = 1;
        this.max_matters = 1;
        this.max_levels = 1;
        this.show_selectors =
          "campuses egress_profiles levels matters periods study_units student_status";
        this.required_selectors =
          "egress_profiles levels matters periods study_units";
      }
      if (this.selected_report_type == 6) {
        this.max_egress_profiles = 1;
        this.use_calculated_cohorts = true;
        this.show_selectors =
          "campuses egress_profiles cohorts cohort_index student_status";
        this.required_selectors = "egress_profiles cohorts";
      }
      if (this.selected_report_type == 7) {
        // this.max_egress_profiles = 1;
        // this.max_matters = 1;
        // this.max_periods = 1;
        // this.max_sections = 1;
        // this.max_professors = 1;
        this.show_selectors =
          "campuses egress_profiles levels matters periods sections professors is_active_section";
        this.required_selectors = "is_active_section";
      }
      if (this.selected_report_type == 8) {
        this.max_egress_profiles = 1;
        this.use_calculated_cohorts = true;
        this.show_selectors =
          "campuses egress_profiles cohorts cohort_index student_status";
        this.required_selectors = "egress_profiles cohorts";
      }
      this.clearUnusedFilters(this.selected_report_type);
      this.syncFiltersToURL();
    },
    campuses_() {
      this.syncFiltersToURL();
    },
    egress_profiles_() {
      this.syncFiltersToURL();
    },
    levels_() {
      this.syncFiltersToURL();
    },
    periods_() {
      this.syncFiltersToURL();
    },
    matters_() {
      this.syncFiltersToURL();
    },
    sections_() {
      this.syncFiltersToURL();
    },
    study_units_() {
      this.syncFiltersToURL();
    },
    cohorts_() {
      this.syncFiltersToURL();
    },
    professors_() {
      this.syncFiltersToURL();
    },
    cycles_() {
      this.syncFiltersToURL();
    },
    student_status_() {
      this.syncFiltersToURL();
    },
    is_active_section_() {
      this.syncFiltersToURL();
    },
  },
  mounted() {},
  created() {
    this.$hasPermissions();
    this.$store.dispatch(names.FETCH_CAMPUSES);
    this.$store.dispatch(names.FETCH_FACULTIES);
    this.$store.dispatch(names.FETCH_CAREERS);
    this.$store.dispatch(names.FETCH_EGRESS_PROFILES);
    this.$store.dispatch(names.FETCH_MODALITIES);
    this.$store.dispatch(names.FETCH_PERIODS);
    this.$store.dispatch(names.FETCH_CYCLES);
    this.restoreFiltersFromURL();
  },
  beforeDestroy() {
    EventBus.$off("report_request_finished", this.onReportRequestFinished);
  },
};
</script>

<style scoped>
/* ---------- Contenedor ---------- */
.general-report-container {
  width: 100%;
  max-width: 100%;
  padding: var(--kl-space-5);
}

/* ---------- Hero header ---------- */
.report-hero {
  display: flex;
  align-items: center;
  gap: var(--kl-space-4);
  padding: var(--kl-space-5) var(--kl-space-6);
  border-radius: var(--kl-radius-lg);
  background: linear-gradient(
    120deg,
    var(--kl-color-primary-deep),
    var(--kl-color-primary)
  );
  color: var(--kl-text-on-primary);
  box-shadow: var(--kl-shadow-lg);
  margin-bottom: var(--kl-space-5);
  text-align: left;
}
.report-hero__icon {
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  border-radius: var(--kl-radius-lg);
  background: rgba(255, 255, 255, 0.16);
}
.report-hero__title {
  margin: 0;
  font-size: var(--kl-fs-title-lg);
  font-weight: var(--kl-fw-bold);
  line-height: var(--kl-lh-tight);
  color: var(--kl-text-on-primary);
}
.report-hero__subtitle {
  margin: var(--kl-space-1) 0 0;
  font-size: var(--kl-fs-body-lg);
  font-weight: var(--kl-fw-regular);
  opacity: 0.92;
}

/* ---------- Tarjetas de paso ---------- */
.report-picker,
.filters-card {
  background: var(--kl-card-bg);
  border: 1px solid var(--kl-card-border);
  border-radius: var(--kl-card-radius);
  box-shadow: var(--kl-card-shadow);
  padding: var(--kl-space-5);
  margin-bottom: var(--kl-space-5);
}
.step-head {
  display: flex;
  align-items: center;
  gap: var(--kl-space-3);
  margin-bottom: var(--kl-space-4);
}
.step-badge {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--kl-color-primary);
  color: var(--kl-text-on-primary);
  font-weight: var(--kl-fw-bold);
  font-size: var(--kl-fs-body);
}
.step-title {
  margin: 0;
  font-size: var(--kl-fs-title-sm);
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-heading);
}

/* ---------- Tarjetas de tipo de reporte ---------- */
.report-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: var(--kl-space-3);
}
.report-tile {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--kl-space-3);
  text-align: left;
  padding: var(--kl-space-4);
  border: 1px solid var(--kl-border-soft);
  border-radius: var(--kl-radius-md);
  background: var(--kl-surface);
  color: var(--kl-text-body);
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease, background 0.18s ease;
}
.report-tile:hover {
  border-color: var(--kl-color-accent-border);
  background: var(--kl-surface-muted);
  box-shadow: var(--kl-shadow-sm);
  transform: translateY(-2px);
}
.report-tile:focus {
  outline: none;
  box-shadow: var(--kl-shadow-focus);
}
.report-tile--active {
  border-color: var(--kl-color-primary);
  background: var(--kl-color-accent-soft);
  box-shadow: var(--kl-shadow-focus);
}
.report-tile__icon {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--kl-radius-md);
  background: var(--kl-color-accent-soft);
  color: var(--kl-color-primary);
  font-size: 1.15rem;
}
.report-tile--active .report-tile__icon {
  background: var(--kl-color-primary);
  color: var(--kl-text-on-primary);
}
.report-tile__label {
  font-size: var(--kl-fs-body);
  font-weight: var(--kl-fw-semibold);
  line-height: var(--kl-lh-tight);
}
.report-tile__check {
  position: absolute;
  top: var(--kl-space-2);
  right: var(--kl-space-2);
  color: var(--kl-color-primary);
  font-size: 1rem;
}

/* ---------- Botones de acción ---------- */
.action-buttons {
  display: flex;
  gap: var(--kl-space-3);
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  margin-top: var(--kl-space-5);
  padding-top: var(--kl-space-4);
  border-top: 1px solid var(--kl-border-soft);
}

/* ---------- Estado inicial / bienvenida ---------- */
.welcome-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--kl-space-6);
  background: var(--kl-card-bg);
  border: 1px solid var(--kl-card-border);
  border-radius: var(--kl-card-radius);
  box-shadow: var(--kl-card-shadow);
  padding: var(--kl-space-6);
}
.welcome-content {
  flex: 1 1 auto;
  max-width: 640px;
  text-align: left;
}
.welcome-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: var(--kl-radius-lg);
  background: var(--kl-color-accent-soft);
  color: var(--kl-color-primary);
  font-size: 1.6rem;
  margin-bottom: var(--kl-space-4);
}
.welcome-title {
  font-size: var(--kl-fs-title-md);
  font-weight: var(--kl-fw-bold);
  color: var(--kl-text-heading);
  line-height: var(--kl-lh-tight);
  margin-bottom: var(--kl-space-3);
}
.welcome-description {
  font-size: var(--kl-fs-body);
  line-height: var(--kl-lh-base);
  color: var(--kl-text-body);
  margin-bottom: var(--kl-space-5);
}
.welcome-features {
  display: flex;
  flex-direction: column;
  gap: var(--kl-space-3);
  list-style: none;
  padding: 0;
  margin: 0;
}
.feature-item {
  display: flex;
  align-items: flex-start;
  gap: var(--kl-space-3);
  font-size: var(--kl-fs-body);
  line-height: var(--kl-lh-base);
  color: var(--kl-text-body);
}
.feature-item__icon {
  flex: 0 0 auto;
  color: var(--kl-color-secondary);
  font-size: 1.1rem;
  margin-top: 0.15rem;
}
.welcome-illustration {
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
}
.welcome-image {
  max-width: 280px;
  height: auto;
  border-radius: var(--kl-radius-lg);
}

/* ---------- Responsive ---------- */
@media (max-width: 768px) {
  .general-report-container {
    padding: var(--kl-space-4);
  }
  .report-hero {
    flex-direction: column;
    text-align: center;
    padding: var(--kl-space-5) var(--kl-space-4);
  }
  .welcome-card {
    flex-direction: column-reverse;
  }
  .welcome-image {
    max-width: 200px;
  }
}

/* ---------- Impresión ---------- */
@media print {
  .report-hero {
    background: none !important;
    color: #000 !important;
    box-shadow: none;
    padding: 0;
    margin-bottom: var(--kl-space-3);
  }
  .report-hero__icon {
    display: none;
  }
  .report-hero__title {
    color: #000 !important;
  }
}
</style>